import { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { generateUniqId } from '@/helpers/uuid';
import { useModal } from '@/providers/modal';
import { MyTooltip } from '@/webkit/MyTooltip';
import Analytics from '@/analyticsGA';
import { useBrandSelectionStatus } from '@/pages/InvestingPage/hooks/useBrandSelectionStatus';
import { useSaveBrandsForInvestment } from '@/query/queries/investing/useSaveBrandsForInvestment';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { Badge, Button, Checkbox, Snackbar } from '@scramble/ui';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { Image } from '@/components/Image';
import { useWindowSize } from '@/hooks/useWindowSize';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { ERROR_CODES } from '@/helpers/constants';
import s from './index.module.scss';

const id = MODAL_IDS.brands_for_investment;

export const BrandsForInvestmentModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

export const Modal = ({ wizardId }) => {
  const { isMobile } = useWindowSize();
  const { closeModal } = useModal();
  const {
    data,
    hasData,
    filledBrandCount,
    availableForSelectionBrandList,
    selectedAndFilledBrandList,
    totalAvailableCount,
    newBrandList,
    newBrands,
  } = useBrandSelectionStatus();
  const { t } = useTranslation();
  const initialSelection = useRef(false);
  const { mutateAsync: saveBrands } = useSaveBrandsForInvestment();
  const modalId = useRef(generateUniqId());

  const { values, setFieldValue, handleSubmit } = useFormik({
    onSubmit: async values => {
      const payload = [
        ...values.selected,
        ...selectedAndFilledBrandList.map(item => item.id),
      ];

      Analytics.brandsSelectionSaved({
        wizard_id: wizardId,
        modal_id: modalId.current,
        included_count: payload.length,
        excluded_count: data?.list.length - payload.length,
        filled_brands_count: filledBrandCount,
        new_brands_selected: newBrandList.reduce((count, brand) => {
          if (payload.includes(brand.id)) count++;
          return count;
        }, 0),
      });

      try {
        const res = await saveBrands(payload);
        if (res?.list) {
          Snackbar({ type: 'success', text: t('modal:brands_for_investment_modal.saved') });
          closeModal(id);
        }
      } catch (err) {
        const text =
          err?.code === ERROR_CODES.brand_unavailable
            ? t('modal:brands_for_inv_status.brand_unavailable_desc')
            : err?.message;
        Snackbar({ type: 'danger', text });
      }
    },
    initialValues: {
      selected: [],
    },
  });

  useEffect(() => {
    hasData &&
      Analytics.selectBrandsModalShow({
        modal_id: modalId.current,
        wizard_id: wizardId,
        new_brands_exist: !!newBrands,
        new_brands_count: newBrands,
      });
  }, [hasData]);

  useEffect(() => {
    const backdrop = document.querySelector('[data-role="modal-backdrop"]');
    const closeX = document.querySelector('[data-role="modal-close-x"]');
    const backdropHandler = () => {
      Analytics.selectBrandsModalDismissed({
        wizard_id: wizardId,
        modal_id: modalId.current,
        exit_type: 'outside_click',
      });
    };
    const closeXHandler = () => {
      Analytics.selectBrandsModalDismissed({
        wizard_id: wizardId,
        modal_id: modalId.current,
        exit_type: 'x_close',
      });
    };
    backdrop?.addEventListener('click', backdropHandler);
    closeX?.addEventListener('click', closeXHandler);
    return function cleanup() {
      backdrop?.removeEventListener('click', backdropHandler);
      closeX?.removeEventListener('click', closeXHandler);
    };
  }, [isMobile]);

  useEffect(() => {
    if (availableForSelectionBrandList.length && !initialSelection.current) {
      setFieldValue(
        'selected',
        availableForSelectionBrandList
          .filter(item => item.selected)
          .map(item => item.id),
      );
      initialSelection.current = true;
    }
  }, [availableForSelectionBrandList]);

  if (!data) return null;

  const content = (
    <form className={s.modal} onSubmit={handleSubmit}>
      <div className={s.header}>
        <h1 className="f-headline-2xs f-500">
          {t('modal:brands_for_investment_modal.select_brands')}
        </h1>
        <p className="f-body-sm">
          {t('modal:brands_for_investment_modal.exclude_brands', {
            fee: data.fee,
          })}
        </p>
      </div>
      <div className={s.content}>
        <div className={s.content_inner}>
          {data.list.map(item => (
            <div
              key={item.id}
              className={classnames(s.brand, item.is_filled && s.brand_filled)}
            >
              <div className={s.brand_content}>
                <div className={s.brand_icon}>
                  <Image
                    image={item.logo}
                    preset="selectionLogo"
                    alt={item.name}
                    width={24}
                    height={24}
                  />
                </div>
                <div className={classnames('f-body-md', s.brand_name)}>
                  {item.name}
                </div>
              </div>

              {item.is_new && !item.is_filled && (
                <Badge
                  type="success"
                  appearance="filled"
                  size="small"
                  label={t('modal:brands_for_investment_modal.new')}
                />
              )}

              {item.is_filled ? (
                <MyTooltip
                  theme="dark"
                  renderContent={() => (
                    <div className={classnames('f-body-sm', s.tooltip_filled)}>
                      {t('modal:brands_for_investment_modal.filled_tooltip')}
                    </div>
                  )}
                  renderElement={() => (
                    <Badge
                      type="neutral"
                      appearance="tonal"
                      size="small"
                      label={t('modal:brands_for_investment_modal.filled')}
                      onMouseOver={() =>
                        Analytics.brandsFilledModalTt({
                          wizard_id: wizardId,
                          modal_id: modalId.current,
                        })
                      }
                    />
                  )}
                />
              ) : (
                <Checkbox
                  name={`item_${item.id}`}
                  checked={values.selected.includes(item.id)}
                  onChange={() => {
                    const isChecked = values.selected.includes(item.id);
                    setFieldValue(
                      'selected',
                      isChecked
                        ? values.selected.filter(i => i !== item.id)
                        : [...values.selected, item.id],
                    );
                    isChecked
                      ? Analytics.brandExcluded({
                          wizard_id: wizardId,
                          modal_id: modalId.current,
                          brand_id: item.id,
                          is_new: item.is_new,
                        })
                      : Analytics.brandIncluded({
                          wizard_id: wizardId,
                          modal_id: modalId.current,
                          brand_id: item.id,
                          is_new: item.is_new,
                        });
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className={s.footer}>
        <div className={s.selection_wrapper}>
          <div
            className={classnames(
              s.selection_info,
              values.selected.length !== totalAvailableCount &&
                s.selection_info_button,
            )}
          >
            <span className={s.selection_count}>
              {values.selected.length === totalAvailableCount
                ? t('modal:brands_for_investment_modal.brands_selected', {
                    count: values.selected.length,
                  })
                : values.selected.length}
            </span>
            {values.selected.length !== totalAvailableCount && (
              <Button
                theme="neutral"
                appearance="filled"
                size="sm"
                className={s.selection_button}
                onClick={() => {
                  Analytics.brandsSelectAllClicked({
                    wizard_id: wizardId,
                    modal_id: modalId.current,
                  });
                  setFieldValue(
                    'selected',
                    availableForSelectionBrandList.map(item => item.id),
                  );
                }}
              >
                {t('modal:brands_for_investment_modal.select_all_brands')}
              </Button>
            )}
          </div>
        </div>

        <Button
          theme="neutral"
          appearance="tonal"
          size="lg"
          onClick={() => {
            Analytics.selectBrandsModalDismissed({
              wizard_id: wizardId,
              modal_id: modalId.current,
              exit_type: 'cancel_button',
            });
            closeModal(id);
          }}
        >
          {t('modal:brands_for_investment_modal.cancel')}
        </Button>
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          type="submit"
          disabled={values.selected.length < 1}
        >
          {t('modal:brands_for_investment_modal.save_selection')}
        </Button>
      </div>
    </form>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};
